import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  useDeleteContractMutation,
  useUpdateContractMutation,
} from "@src/__generated__/graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { DataTableActionButton } from "@src/components/ui-kit/DataTable/DataTableActionButton";
import { onError } from "@src/utils/apollo";
import { can, withCan } from "@src/utils/components/permissions";
import { FormMode } from "@src/utils/types";
import { observer } from "mobx-react-lite";
import React, {
  type FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { ModalEditContractForm } from "../../form/contract/ModalEditContractForm";
import { StoreContractForm } from "../../form/contract/StoreContractForm";
import type { ContractModel } from "../../models";
import { DocumentsCtx } from "../../StoreDocuments";

export const PopoverRowActions: FunctionComponent<
  React.PropsWithChildren<{ contract: ContractModel }>
> = withCan(
  { do: ["contract_update", "contract_delete"], fail: false },
  observer(function PopoverRowActions({ contract }) {
    const pageStore = useContext(DocumentsCtx);

    const [store] = useState(() => {
      const store = StoreContractForm.create();
      return store;
    });

    const editModal = useDisclosure();
    const delModal = useDisclosure();

    const [update, updateReq] = useUpdateContractMutation({
      onCompleted() {
        pageStore.refetchContracts?.();
        editModal.onClose();
      },
      ...onError(),
    });

    const [del] = useDeleteContractMutation({
      variables: { id: contract.id },
      onCompleted() {
        pageStore.refetchContracts?.();
        delModal.onClose();
      },
      ...onError(),
    });

    useEffect(() => {
      if (editModal.isOpen) store.fillContractForm(contract);
    }, [editModal.isOpen]);

    return (
      <React.Fragment>
        <Menu>
          <MenuButton as={DataTableActionButton} />
          <MenuList>
            {can("contract_update") && (
              <MenuItem {...editModal.getButtonProps()}>
                <Trans>Edit</Trans>
              </MenuItem>
            )}
            {can("contract_delete") && (
              <MenuItem {...delModal.getButtonProps()}>
                <Trans>Delete</Trans>
              </MenuItem>
            )}
          </MenuList>
        </Menu>
        <ModalConfirm
          {...delModal.getDisclosureProps()}
          {...delModal}
          onConfirm={del}
        />
        <ModalEditContractForm
          {...editModal.getDisclosureProps()}
          {...editModal}
          store={store}
          mode={FormMode.EDIT}
          title={t`Edit contract`}
          primaryActionContent={t`Save changes`}
          loading={updateReq.loading}
          defaultContractorName={contract.contractors?.[0]?.legalName}
          onSubmit={(vars) => {
            update({ variables: { input: { ...vars, id: contract.id } } });
          }}
        />
      </React.Fragment>
    );
  }),
);
